import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  listenForMessages,
  requestNotificationPermission,
} from "@/app/services/firebase/firebaseConfig";

export interface AppNotification {
  id: number;
  type: string;
  title: string;
  timestamp: string;
  category: string;
  isRead: boolean;
  isImportant?: boolean;
}

type PermissionStatus = "default" | "granted" | "denied" | "unsupported";

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  permissionStatus: PermissionStatus;
  addNotification: (n: Omit<AppNotification, "id" | "isRead">) => void;
  markAllAsRead: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [permissionStatus, setPermissionStatus] =
    useState<PermissionStatus>("default");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio("/sounds/notification-bell.mp3");
    audioRef.current.volume = 0.5;
  }, []);

  const playBellSound = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch((err) => {
      console.warn("Notification sound blocked:", err);
    });
  };

  const addNotification = (n: Omit<AppNotification, "id" | "isRead">) => {
    setNotifications((prev) => [
      { ...n, id: Date.now(), isRead: false },
      ...prev,
    ]);
    playBellSound();
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setPermissionStatus("unsupported");
      return;
    }

    setPermissionStatus(Notification.permission as PermissionStatus);

    requestNotificationPermission().then((token) => {
      setPermissionStatus(Notification.permission as PermissionStatus);
      if (token) {
        // console.log("FCM token:", token);
      }
    });

    listenForMessages((payload) => {
      const { title, body } = payload.notification || {};
      addNotification({
        type: payload.data?.type || "Transaction",
        title: body || title || "New notification",
        timestamp: new Date().toLocaleString(),
        category: "Today",
        isImportant: payload.data?.isImportant === "true",
      });
    });
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        permissionStatus,
        addNotification,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return ctx;
};
